import { useState, useCallback } from 'react';
import {
  useAnniversaryPlanSummary,
  useAnniversaryPlanInstallments,
  useSyncAnniversaryPlan,
} from '../../hooks/useAnniversaryPlan';
import { ITEMS_PER_PAGE } from '../constants/anniversaryPlanConstants';

export const useAnniversaryPlanFeature = () => {
  const [filters, setFilters] = useState({
    contract_number: '',
    has_overdue: '',
    page: 1,
    per_page: ITEMS_PER_PAGE,
  });

  const [rowsPerPage, setRowsPerPage] = useState(ITEMS_PER_PAGE);

  const [pendingFilters, setPendingFilters] = useState({
    contract_number: '',
    has_overdue: '',
  });

  const [selectedContract, setSelectedContract] = useState(null);
  const [installmentsDialogOpen, setInstallmentsDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const summaryParams = {
    ...(filters.contract_number && { contract_number: filters.contract_number }),
    ...(filters.has_overdue !== '' && { has_overdue: filters.has_overdue }),
    page: filters.page,
    per_page: filters.per_page,
  };

  const { data: summaryData, isLoading: summaryLoading } = useAnniversaryPlanSummary(summaryParams);

  const installmentsParams = selectedContract
    ? { contract_number: selectedContract.contract_number, per_page: 200 }
    : {};

  const { data: installmentsData, isLoading: installmentsLoading } =
    useAnniversaryPlanInstallments(installmentsParams);

  const syncMutation = useSyncAnniversaryPlan();

  const results = summaryData?.results ?? [];
  const total = summaryData?.total ?? 0;
  const totalPages = Math.ceil(total / filters.per_page) || 0;

  const handleFilterChange = useCallback((event) => {
    const { name, value } = event.target;
    setPendingFilters((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSearch = useCallback(() => {
    setFilters((prev) => ({
      ...prev,
      ...pendingFilters,
      page: 1,
    }));
  }, [pendingFilters]);

  const handlePageChange = useCallback((event, value) => {
    setFilters((prev) => ({ ...prev, page: value }));
  }, []);

  const handleRowsPerPageChange = useCallback((event) => {
    const value = parseInt(event.target.value, 10);
    setRowsPerPage(value);
    setFilters((prev) => ({ ...prev, per_page: value, page: 1 }));
  }, []);

  const handleRowClick = useCallback((row) => {
    setSelectedContract(row);
    setInstallmentsDialogOpen(true);
  }, []);

  const handleCloseInstallmentsDialog = useCallback(() => {
    setInstallmentsDialogOpen(false);
    setSelectedContract(null);
  }, []);

  const handleSync = useCallback(async () => {
    try {
      const result = await syncMutation.mutateAsync();
      setSnackbar({
        open: true,
        message: `Sincronización completada: ${result?.result?.changes_detected ?? 0} cambios detectados`,
        severity: 'success',
      });
    } catch {
      setSnackbar({
        open: true,
        message: 'Error al sincronizar con Google Sheets',
        severity: 'error',
      });
    }
  }, [syncMutation]);

  const handleCloseSnackbar = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  return {
    results,
    total,
    totalPages,
    isLoading: summaryLoading,
    page: filters.page,
    rowsPerPage,
    pendingFilters,
    installmentsData,
    installmentsLoading,
    installmentsDialogOpen,
    selectedContract,
    syncLoading: syncMutation.isPending,
    snackbar,
    handleFilterChange,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
    handleRowClick,
    handleCloseInstallmentsDialog,
    handleSync,
    handleCloseSnackbar,
  };
};
